import { cx } from '@linaria/core';
import { useRef, useEffect } from 'react';
import { pm2ConfigStore } from '@pm2/store';
import { Link } from '@provider/navigation';
import { Tooltip } from '@ui/kit';
import { CrossIcon, SearchIcon } from '@ui/kit/icons';
import { useLoaderState } from '@utils/react';
import { inputWrapperCls, crossIconCls, searchThrobberCls, crossButtonCls, inputCls, goToSearchLinkCls } from './atom.input.style.js';
import { useFocusOnEnter } from './hook.focus-on-enter.js';
import { TRANSITION_DURATION_MS } from './root.quicksearch.style.js';
import { jsxs, jsx } from 'react/jsx-runtime';

function Input({
  value,
  visible,
  isLoading,
  selectedResultId,
  onClose,
  onInput: onInputCallback
}) {
  const inputRef = useRef(null);
  const showThrobber = useLoaderState(isLoading, {
    delayMs: 0,
    durationMs: 500
  });
  const searchPath = pm2ConfigStore.useSelector(state => state.config.searchPath);
  const handleEscapePressed = () => {
    const input = inputRef.current;
    if (input !== document.activeElement || input?.value.length === 0) {
      onClose();
    }
  };
  const handleCrossClick = evt => {
    evt.preventDefault();
    onClose();
  };
  const onInput = evt => {
    const input = inputRef.current;
    if (input) {
      onInputCallback(evt.currentTarget?.value ?? "");
    }
  };
  useFocusOnEnter(inputRef, visible, TRANSITION_DURATION_MS);
  useKeyListener(visible, handleEscapePressed);
  return /*#__PURE__*/jsxs("div", {
    className: inputWrapperCls,
    children: [/*#__PURE__*/jsxs("button", {
      className: cx(crossButtonCls, showThrobber.current && "loading"),
      onClick: handleCrossClick,
      "aria-label": "Luk søgebjælke",
      children: [/*#__PURE__*/jsx(CrossIcon, {
        height: 15,
        className: crossIconCls
      }), /*#__PURE__*/jsx("span", {
        className: searchThrobberCls
      })]
    }), /*#__PURE__*/jsx("input", {
      value: value,
      ref: inputRef,
      type: "search",
      "aria-label": "S\xF8g i indholdet p\xE5 portalen",
      placeholder: "Søg i portalen",
      className: inputCls,
      onChange: onInput,
      "aria-autocomplete": "list",
      "aria-controls": "quicksearch-listbox",
      "aria-activedescendant": selectedResultId
    }), /*#__PURE__*/jsx(Tooltip, {
      ariaType: "label",
      placement: "bottom",
      body: "G\xE5 til udvidet s\xF8geresultatside med din nuv\xE6rende s\xF8gning",
      disabled: value.length === 0,
      children: ref => /*#__PURE__*/jsx(Link, {
        ref: ref,
        href: value.length > 0 ? `${searchPath}?q=${encodeURIComponent(value)}` : undefined,
        className: cx(goToSearchLinkCls),
        onClick: onClose,
        children: /*#__PURE__*/jsx(SearchIcon, {
          height: 20
        })
      })
    })]
  });
}
function useKeyListener(active, onEscape) {
  useEffect(() => {
    if (!active) {
      return;
    }
    const onKeyDown = evt => {
      switch (evt.key) {
        case "Escape":
          onEscape();
          break;
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [active, onEscape]);
}

export { Input };
//# sourceMappingURL=atom.input.js.map
